import { ReactNode, useEffect, useRef } from "react";
import { useBranding } from "../lib/branding";
import { PRIVACY_URL } from "../lib/links";

/** Small inline stroke icon used across the auth screens. */
export const Icon = ({ children }: { children: ReactNode }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);

/**
 * Shared frame for every pre-auth screen (sign in, forgot password, invite /
 * reset): the textured brand hero on the left, a frosted glass card on the
 * right carrying the page's own form, then the Powered-by mark and copyright.
 * Keeps the whole flow visually identical; each page only supplies its card
 * body via `children`.
 */
export const AuthShell = ({
    eyebrow,
    title,
    children,
}: {
    eyebrow?: string;
    title: string;
    children: ReactNode;
}) => {
    const branding = useBranding();
    const tiltRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const tilt = tiltRef.current, card = cardRef.current;
        if (!tilt || !card) return;
        const reduce = window.matchMedia("(prefers-reduced-motion:reduce)").matches;
        const coarse = window.matchMedia("(pointer:coarse)").matches;
        if (reduce || coarse) return;
        let raf = 0;
        const move = (e: PointerEvent) => {
            if (raf) return;
            raf = requestAnimationFrame(() => {
                raf = 0;
                const r = tilt.getBoundingClientRect();
                const px = (e.clientX - r.left) / r.width - 0.5;
                const py = (e.clientY - r.top) / r.height - 0.5;
                card.style.transform = `rotateY(${(px * 5).toFixed(2)}deg) rotateX(${(-py * 5).toFixed(2)}deg) translateY(-2px)`;
            });
        };
        const leave = () => { card.style.transform = ""; };
        tilt.addEventListener("pointermove", move);
        tilt.addEventListener("pointerleave", leave);
        return () => { tilt.removeEventListener("pointermove", move); tilt.removeEventListener("pointerleave", leave); };
    }, []);

    return (
        <div className="tcl-page">
            <section className="tcl-brand" aria-label={branding.orgName}>
                <img className="tcl-brand-logo" src="/brand/logo-horizontal-white.png" alt={branding.orgName} />
                <div className="tcl-brand-copy">
                    <span className="tcl-accent" aria-hidden="true" />
                    <h2>Reliable clinical care, every shift.</h2>
                    <p>The clinical nursing partner for registered NDIS providers — consistency, accountability and respect.</p>
                </div>
            </section>

            <main className="tcl-auth">
                <div className="tcl-tilt" ref={tiltRef}>
                    <div className="tcl-card" ref={cardRef}>
                        {eyebrow && <span className="tcl-eyebrow">{eyebrow}</span>}
                        <h1>{title}</h1>
                        {children}
                        <div className="tcl-powered">
                            <img src="/brand/icon.png" alt="" aria-hidden="true" />
                            <span>Powered by TrustCare</span>
                        </div>
                    </div>
                </div>
                <p className="tcl-copyright">
                    © {branding.orgName} · <a href={PRIVACY_URL} target="_blank" rel="noopener">Privacy</a>
                </p>
            </main>
        </div>
    );
};
